/**
 * Hook for API calls with loading/error states
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext.jsx';
import { APIError } from '../utils/api/core.js';

export const useApiData = ({ apiFunction, options = {}, dependencies = [] }) => {
    const {
        immediate = true,
        showErrorToast = true,
        showSuccessToast = false,
        successMessage = 'Operation completed successfully',
        transform = null,
        retryAttempts = 0,
        retryDelay = 1000,
        shouldRetry = null,
        onSuccess = null,
    } = options;

    const toast = useToast();

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(immediate);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    const abortControllerRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const executeRequestRef = useRef(null);
    // Monotonic request id. The AbortController isn't threaded into the network
    // layer, so a superseded request isn't truly cancelled — guard every state
    // commit on this so a slow earlier response can't overwrite a newer one
    // (stale-data render on rapid dependency changes).
    const requestSeqRef = useRef(0);

    const cleanup = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Explicit null: a 0 handle is falsy, and clear() relies on this to stop a retry.
        if (retryTimeoutRef.current !== null) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);

    // Every path that abandons an in-flight request: supersede it so a late response
    // cannot commit, and clear the spinner its seq-guarded finally will no longer clear.
    const abandonInFlight = useCallback(() => {
        cleanup();
        requestSeqRef.current += 1;
        setIsLoading(false);
    }, [cleanup]);

    // Determine if retry should happen
    const shouldAttemptRetry = useCallback(
        (err, currentRetryCount) => {
            if (currentRetryCount >= retryAttempts) return false;

            if (shouldRetry) {
                return shouldRetry(err, currentRetryCount);
            }

            // Default retry logic
            if (err instanceof APIError) {
                return err.isRetryable();
            }

            // Retry network errors
            return err.name === 'TypeError' || err.message.includes('fetch');
        },
        [retryAttempts, shouldRetry]
    );

    // Execute API function with error handling.
    // Returns a Promise and only ever calls setState inside .then/.catch/.finally
    // callbacks (never synchronously in the function body). This keeps the
    // hook callable from useEffect without tripping react-hooks/set-state-in-effect.
    const executeRequest = useCallback(
        (retryAttempt = 0, executeOptions = {}) => {
            cleanup();
            abortControllerRef.current = new AbortController();
            // A retry continues the same logical request, so only bump the
            // sequence for a fresh (non-retry) call.
            const seq = retryAttempt > 0 ? requestSeqRef.current : (requestSeqRef.current += 1);

            return Promise.resolve()
                .then(() => {
                    // A cancel or clear between scheduling and this microtask supersedes
                    // us; starting would raise a spinner the guarded finally never lowers.
                    if (!isMountedRef.current || seq !== requestSeqRef.current) return;
                    setIsLoading(true);
                    setError(null);
                    if (retryAttempt > 0) {
                        setRetryCount(retryAttempt);
                    }
                    if (!apiFunction) {
                        throw new Error('API function is required');
                    }
                    return apiFunction(executeOptions);
                })
                .then(result => {
                    // Ignore a superseded (out-of-order) response.
                    if (!isMountedRef.current || seq !== requestSeqRef.current) return false;
                    const finalData = transform ? transform(result) : result;
                    setData(finalData);
                    setRetryCount(0);
                    if (showSuccessToast && toast && toast.success) {
                        toast.success(successMessage);
                    }
                    if (onSuccess) onSuccess(finalData);
                    return true;
                })
                .catch(err => {
                    if (!isMountedRef.current || seq !== requestSeqRef.current) return false;
                    if (err.name === 'AbortError') return false;

                    setError(err);

                    if (shouldAttemptRetry(err, retryAttempt)) {
                        retryTimeoutRef.current = setTimeout(() => {
                            if (isMountedRef.current && executeRequestRef.current) {
                                executeRequestRef.current(retryAttempt + 1, executeOptions);
                            }
                        }, retryDelay);
                        return false;
                    }

                    if (showErrorToast) {
                        let errorMessage = 'An unexpected error occurred';
                        if (err instanceof APIError) {
                            errorMessage = err.message;
                        } else if (err.message) {
                            errorMessage = err.message;
                        }
                        if (toast && toast.error) {
                            toast.error(errorMessage);
                        }
                    }
                    return false;
                })
                .finally(() => {
                    if (isMountedRef.current && seq === requestSeqRef.current) {
                        setIsLoading(false);
                    }
                });
        },
        [
            apiFunction,
            transform,
            showSuccessToast,
            successMessage,
            showErrorToast,
            shouldAttemptRetry,
            retryDelay,
            toast,
            cleanup,
            onSuccess,
        ]
    );

    // Mirror executeRequest into a ref so the retry timer can call it without
    // referencing it before declaration.
    useEffect(() => {
        executeRequestRef.current = executeRequest;
    }, [executeRequest]);

    // Resolves true only when fresh data was committed. Errors are swallowed here,
    // so a resolved promise on its own does not mean the fetch succeeded.
    const execute = useCallback(
        (executeOptions = {}) => {
            return executeRequest(0, executeOptions);
        },
        [executeRequest]
    );

    // Retry function
    const retry = useCallback(() => {
        if (error) {
            executeRequest(0);
        }
    }, [error, executeRequest]);

    // Refresh function (alias for execute)
    const refresh = useCallback(() => {
        return execute();
    }, [execute]);

    // Effect for automatic execution
    useEffect(() => {
        if (immediate) {
            executeRequest(0);
        }

        // Dependencies changed, so abandon the previous request: with immediate false
        // nothing re-runs to bump the sequence and a late response would still commit.
        return () => {
            abandonInFlight();
        };
    }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup on unmount
    useEffect(() => {
        // Ensure mounted ref is true when component mounts
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            cleanup();
        };
    }, [cleanup]); // Include cleanup dependency

    return {
        /** Current data */
        data,

        /** Loading state */
        isLoading,

        /** Error state */
        error,

        /** Current retry count */
        retryCount,

        /** Manual execution function */
        execute,

        /** Retry failed request */
        retry,

        /** Refresh data (alias for execute) */
        refresh,

        /** Clear current data and error */
        clear: useCallback(() => {
            abandonInFlight();
            setData(null);
            setError(null);
            setRetryCount(0);
        }, [abandonInFlight]),

        /** Cancel ongoing request */
        cancel: abandonInFlight,
    };
};

/**
 * Simple API data fetching hook
 *
 * @param {Function} apiFunction - API function to execute
 * @param {Array} dependencies - Dependencies for re-execution
 * @returns {Object} Hook state
 */
export const useApiCall = (apiFunction, dependencies = []) => {
    return useApiData({
        apiFunction,
        dependencies,
        options: {
            immediate: true,
            showErrorToast: true,
        },
    });
};

/**
 * Hook for manual API operations (form submissions, etc.)
 *
 * @param {Function} apiFunction - API function to execute
 * @param {Object} options - Hook options
 * @returns {Object} Hook state with manual execution
 */
export const useApiMutation = (apiFunction, options = {}) => {
    return useApiData({
        apiFunction,
        dependencies: [],
        options: {
            immediate: false,
            showErrorToast: true,
            showSuccessToast: true,
            ...options,
        },
    });
};
